/**
 * <quote-slider> — the review quotes in the reviews + press section.
 *
 * A Swiper-style transform slider: the viewport clips, the rail is a flex track this element moves with
 * translate3d. One unified pointer drag serves mouse, pen AND touch (the finger/cursor drags the track
 * 1:1, a flick throws it, the edges rubber-band, and it snaps to the nearest slide on release). A custom
 * element, not an inline <script>: the Section Rendering API re-injects section HTML on every
 * theme-editor change and never re-executes script tags.
 *
 * Autoplay is driven by the CURRENT dot's fill animation, not a setInterval. The fill runs for
 * `interval`, and its `animationend` advances the slide. Freezing the CSS animation (the `is-frozen`
 * class -> `animation-play-state: paused`) freezes the countdown AND defers the advance in one move, so
 * hover/drag/offscreen pause cleanly and the bar never jumps to the end.
 *
 * WCAG 2.2.2 (Level A): a control that stops the motion, plus hover/focus that only pause it. Under
 * reduced motion the fill has no animation (nothing auto-advances) and moves are instant.
 */
// Wrapped in an IIFE because a section can appear twice on one page; a second top-level `class`
// declaration would be a hard error.
(() => {
const EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';   // the same ease-out the hero + film use
const DRAG_START = 6;                                // px before a drag is claimed (vs a click)
const SNAP_RATIO = 0.28;                             // drag past this fraction of a slide -> advance
const FLICK_V = 0.45;                                // px/ms release velocity that throws to the next slide
const RESISTANCE = 0.35;                             // rubber-band factor past the first/last slide

class QuoteSlider extends HTMLElement {
  connectedCallback() {
    this.viewportEl = this.querySelector('[data-quote-viewport]');
    this.rail = this.querySelector('[data-quote-rail]');
    if (!this.viewportEl || !this.rail) return;

    this.slides = Array.from(this.querySelectorAll('[data-quote-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-quote-dot]'));
    this.pauseBtn = this.querySelector('[data-quote-pause]');
    this.index = 0;

    this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.wantsAutoplay = this.hasAttribute('data-autoplay') && this.slides.length > 1;

    // Freeze reasons are tracked as flags; the countdown runs only when none are active.
    this.userPaused = this.motion.matches;
    this.hovered = false;
    this.focused = false;
    this.offscreen = true;
    // NEVER name this flag `hidden` -- `this.hidden` is the DOM property behind the `hidden` attribute,
    // so assigning it hides the whole slider via the UA `[hidden] { display: none }` rule. Same bug the
    // hero had: a background-tab load (document.hidden === true) blanked the section. See hero-slider.js.
    this.tabHidden = document.hidden;
    this.dragging = false;

    this.querySelector('[data-quote-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-quote-next]')?.addEventListener('click', () => this.step(1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.pauseBtn?.addEventListener('click', () => this.togglePause());

    // Only KEYBOARD focus (:focus-visible) freezes; a mouse click on an arrow/dot leaves the button
    // focused but must not stop autoplay.
    const keyboardFocused = () => {
      const el = document.activeElement;
      if (!el || !this.contains(el)) return false;
      try { return el.matches(':focus-visible'); } catch (e) { return false; }
    };
    this.addEventListener('pointerenter', () => this.setFlag('hovered', true));
    this.addEventListener('pointerleave', () => this.setFlag('hovered', false));
    this.addEventListener('focusin', () => this.setFlag('focused', keyboardFocused()));
    this.addEventListener('focusout', () => {
      if (!this.contains(document.activeElement)) this.setFlag('focused', false);
    });

    this.onVisibility = () => this.setFlag('tabHidden', document.hidden);
    document.addEventListener('visibilitychange', this.onVisibility);

    // Advance when the current dot's fill finishes its countdown.
    this.onFillEnd = (e) => { if (e.animationName === 'snapDotFill' && this.playing) this.step(1); };
    this.addEventListener('animationend', this.onFillEnd);

    this.visObserver = new IntersectionObserver(([e]) => this.setFlag('offscreen', !e.isIntersecting), { threshold: 0.2 });
    this.visObserver.observe(this);

    this.onMotionChange = () => { if (this.motion.matches) this.setPaused(true); };
    this.motion.addEventListener('change', this.onMotionChange);

    // Keep the track aligned to the current slide when the viewport is resized. rAF-guarded so a burst
    // of resize events (which each read clientWidth and write a transform) does not thrash layout.
    this.onResize = () => {
      if (this._resizeRaf) return;
      this._resizeRaf = requestAnimationFrame(() => { this._resizeRaf = null; this.setPos(-this.index * this.slideW, false); });
    };
    window.addEventListener('resize', this.onResize);

    this.initDrag();
    this.classList.toggle('has-autoplay', this.wantsAutoplay);
    this.setPos(0, false);
    this.activate(0);
    this.syncPauseButton();
    this.sync();
  }

  disconnectedCallback() {
    this.visObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.motion?.removeEventListener('change', this.onMotionChange);
    window.removeEventListener('resize', this.onResize);
    this.removeEventListener('animationend', this.onFillEnd);
  }

  get slideW() { return this.viewportEl.clientWidth; }

  /** Move the track. `animate` runs the eased transition; otherwise it snaps instantly (drag / resize). */
  setPos(x, animate) {
    this.pos = x;
    this.rail.style.transition = (animate && !this.motion.matches) ? `transform 500ms ${EASE}` : 'none';
    this.rail.style.transform = `translate3d(${x}px, 0, 0)`;
  }

  /** Go to slide i (wrapping), animated. Every nav path (arrows, dots, autoplay, drag) uses this. */
  go(i, opts = {}) {
    const count = this.slides.length;
    const next = ((i % count) + count) % count;
    this.index = next;
    this.setPos(-next * this.slideW, opts.instant ? false : true);
    this.activate(next);
  }

  /** Adjacent step (arrows + autoplay). Always animated -- with a handful of slides the wrap is just a
   *  short sweep back to the start, and animating it means every click visibly moves (an instant wrap
   *  read as "the arrow did nothing"). */
  step(dir) {
    this.go(this.index + dir);
  }

  activate(i) {
    this.index = i;
    this.dots.forEach((dot, n) => {
      if (n === i) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    this.slides.forEach((s, n) => s.toggleAttribute('data-current', n === i));
    this.restartFill();
  }

  /** Restart the current dot's fill from zero. Reading offsetWidth forces the reflow so the animation
   *  removal lands before it is re-applied. */
  restartFill() {
    const fill = this.dots[this.index]?.querySelector('[data-quote-fill]');
    if (!fill) return;
    fill.style.animation = 'none';
    void fill.offsetWidth;
    fill.style.animation = '';
  }

  /** Unified pointer drag: mouse, pen and touch all drag the track 1:1, with a flick + snap on release
   *  and rubber-band resistance at the ends. touch-action:pan-y (CSS) lets vertical page scroll through;
   *  a horizontal drag claims the gesture and preventDefaults. */
  initDrag() {
    let startX = 0, startY = 0, startPos = 0, id = null;
    let decided = false, active = false, moved = false;
    let lastX = 0, lastT = 0, velocity = 0;

    const down = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      id = e.pointerId;
      startX = lastX = e.clientX; startY = e.clientY;
      startPos = -this.index * this.slideW;
      lastT = e.timeStamp; velocity = 0;
      decided = false; active = false; moved = false;
    };

    const move = (e) => {
      if (id !== e.pointerId) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (!decided) {
        if (Math.abs(dx) < DRAG_START && Math.abs(dy) < DRAG_START) return;
        decided = true;
        if (Math.abs(dy) > Math.abs(dx)) { id = null; return; }   // vertical intent -> let the page scroll
        active = true;
        this.setFlag('dragging', true);
        this.classList.add('is-dragging');
        this.viewportEl.setPointerCapture?.(e.pointerId);
        this.setPos(startPos, false);                              // kill any running transition
      }
      if (!active) return;
      moved = true;
      e.preventDefault();
      const dt = e.timeStamp - lastT;
      if (dt > 0) velocity = (e.clientX - lastX) / dt;             // px/ms, sign = direction
      lastX = e.clientX; lastT = e.timeStamp;
      this.setPos(this.resist(startPos + dx), false);
    };

    const up = (e) => {
      if (id !== e.pointerId) return;
      id = null;
      if (!active) return;
      active = false;
      this.classList.remove('is-dragging');
      this.viewportEl.releasePointerCapture?.(e.pointerId);

      const dx = e.clientX - startX;
      const flick = Math.abs(velocity) > FLICK_V;
      let dir = 0;
      if (dx <= -this.slideW * SNAP_RATIO || (flick && velocity < 0)) dir = 1;
      else if (dx >= this.slideW * SNAP_RATIO || (flick && velocity > 0)) dir = -1;
      const target = Math.min(this.slides.length - 1, Math.max(0, this.index + dir));  // no wrap on drag
      this.go(target);                                            // eased snap
      this.setFlag('dragging', false);
      if (moved) this.suppressClick = true;                       // a drag shouldn't fire a card click
    };

    this.viewportEl.addEventListener('pointerdown', down);
    this.viewportEl.addEventListener('pointermove', move);
    this.viewportEl.addEventListener('pointerup', up);
    this.viewportEl.addEventListener('pointercancel', up);
    this.viewportEl.addEventListener('click', (e) => {
      if (this.suppressClick) { e.preventDefault(); e.stopPropagation(); this.suppressClick = false; }
    }, true);
  }

  /** Rubber-band the track past the first/last slide. */
  resist(x) {
    const min = -(this.slides.length - 1) * this.slideW;
    if (x > 0) return x * RESISTANCE;
    if (x < min) return min + (x - min) * RESISTANCE;
    return x;
  }

  togglePause() { this.setPaused(!this.userPaused); }

  setPaused(paused) {
    this.userPaused = paused;
    this.syncPauseButton();
    this.sync();
    if (!paused) this.restartFill();
  }

  setFlag(name, value) {
    if (this[name] === value) return;
    this[name] = value;
    this.sync();
  }

  syncPauseButton() {
    if (!this.pauseBtn) return;
    this.pauseBtn.setAttribute('aria-pressed', String(this.userPaused));
    this.pauseBtn.setAttribute('aria-label', this.userPaused ? 'Play the reviews' : 'Pause the reviews');
  }

  /** The countdown runs only when autoplay is on and nothing is freezing it. */
  get playing() {
    return this.wantsAutoplay && !this.userPaused && !this.hovered && !this.focused
      && !this.offscreen && !this.tabHidden && !this.dragging;
  }

  /** `is-frozen` pauses the fill animation (freezing the countdown and deferring its animationend)
   *  without removing it, so the bar holds instead of snapping to full. */
  sync() {
    this.classList.toggle('is-frozen', this.wantsAutoplay && !this.playing);
  }
}

if (!customElements.get('quote-slider')) customElements.define('quote-slider', QuoteSlider);
})();
